import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';


const PrototypeAgentReRoutePage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [routingProfiles, setRoutingProfiles] = useState([]);
  const [loadingRoutingProfiles, setLoadingRoutingProfiles] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingProfileQueues, setLoadingProfileQueues] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [updatingAgentId, setUpdatingAgentId] = useState(null);
  const [rowError, setRowError] = useState(null);
  const [profileQueues, setProfileQueues] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  


  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;
  const AGENTS_VIEW_API = apiEndpoints.agentsView;
  const ROUTING_PROFILES_API = apiEndpoints.routingProfiles;
  const UPDATE_AGENT_ROUTING_PROFILE_API = apiEndpoints.updateAgentRoutingProfileApi;
  const PROFILES_AND_QUEUES_API = apiEndpoints.profilesAndQueues;

  // Single agent form state
  const [viewAgentForm, setViewAgentForm] = useState({
    instance_id: '',
  });

   // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    setAgents([]);
    setProfileQueues(null);
    setResult(null);
    setError(null);
    setViewAgentForm({
      instance_id: '',
    });
  };

  const handleViewAgentFormChange = (e) => {
    const { name, value } = e.target;
    
    setViewAgentForm(prev => ({ ...prev, [name]: value }));
    if (name === 'instance_id') {
      fetchRoutingProfiles(value);
      setAgents([]);
      setProfileQueues(null);
      setResult(null);
      setError(null);
    }
  };

 // Fetch agents for a specific instance
  const fetchAgentList = async (instanceId) => {
    setLoadingAgents(true);
    
    setAgents([]);
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiService.post(AGENTS_VIEW_API, {
        instance_id: instanceId,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setAgents(response.data.result?.agents || []);
        setPage(1);
      } else {
        console.error('Failed to load agents:', response.data.message);
        setError('Failed to load agents');
        throw new Error('Failed to load agents');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Error fetching agents: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleViewAgentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      await fetchAgentList(viewAgentForm.instance_id);
      setResult({ status: 'ok' });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to acquire list of agents');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfilesAndQueues = async (e) => {
    e.preventDefault();
    setLoadingProfileQueues(true);
    setProfileQueues(null);
    setError(null);
    setResult(null);
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiService.post(PROFILES_AND_QUEUES_API, {
        instance_id: viewAgentForm.instance_id,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setPreviewTitle('Routing Profiles and Queues JSON Preview');
        setShowPreview(true);
        setProfileQueues(response.data.result);
      } else {
        throw new Error(response.data.result?.message || response.data.message || 'Failed to load profiles and queues');
      }
    } catch (err) {
      setError(err.response?.data?.result?.message || err.response?.data?.message || err.message);
    } finally {
      setLoadingProfileQueues(false);
    }
  };

  const downloadArchiveDocument = () => {
    if (!profileQueues) return;
    const blob = new Blob([JSON.stringify(profileQueues, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${profileQueues?.archiveName || 'RoutingProfilesAndQueues'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toDateString().substring(4) + ' ' + date.toLocaleTimeString();
  };

  const getSortValue = (agent, key) => {
    switch (key) {
      case 'name':
        return [agent.identity?.first_name, agent.identity?.last_name].filter(Boolean).join(' ').toLowerCase();
      case 'username':
        return (agent.username || '').toLowerCase();
      case 'email':
        return (agent.identity?.email || '').toLowerCase();
      case 'last_modified':
        return agent.last_modified_time || 0;
      case 'routing_profile':
        return (agent.routing_profile_id || '').toLowerCase();
      case 'security_profile':
        return (agent.security_profile_ids?.join(', ') || '').toLowerCase();
      case 'phone_type':
        return (agent.phone_config?.phone_type || '').toLowerCase();
      default:
        return '';
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredAgents = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter((agent) => {
      const name = [agent.identity?.first_name, agent.identity?.last_name].filter(Boolean).join(' ').toLowerCase();
      const username = (agent.username || '').toLowerCase();
      const email = (agent.identity?.email || '').toLowerCase();
      return name.includes(term) || username.includes(term) || email.includes(term);
    });
  }, [agents, searchTerm]);

  const sortedAgents = React.useMemo(() => {
    if (!sortKey) return filteredAgents;
    const copy = [...filteredAgents];
    copy.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredAgents, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedAgents.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedAgents = sortedAgents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

   // Fetch routing profiles for a specific instance
  const fetchRoutingProfiles = async (instanceId) => {
    setLoadingRoutingProfiles(true);
    setRoutingProfiles([]);
    setViewAgentForm(prev => ({ ...prev, routing_profile_id: '' }));
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiService.post(ROUTING_PROFILES_API, {
        instance_id: instanceId,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setRoutingProfiles(response.data.routing_profiles || []);
      } else {
        console.error('Failed to load routing profiles:', response.data.message);
        setError('Failed to load routing profiles');
      }
    } catch (err) {
      console.error('Error fetching routing profiles:', err);
      setError('Error fetching routing profiles: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingRoutingProfiles(false);
    }
  };

  const handleAgentRoutingProfileChange = async (agent, newRoutingProfileId) => {
    const previousProfileId = agent.routing_profile_id;
    if (newRoutingProfileId === previousProfileId) return;

    setUpdatingAgentId(agent.id);
    setRowError(null);

    // Optimistically redraw just this agent's select
    setAgents((prev) => prev.map((a) => (
      a.id === agent.id ? { ...a, routing_profile_id: newRoutingProfileId } : a
    )));

    try {
      const response = await apiService.post(UPDATE_AGENT_ROUTING_PROFILE_API, {
        instanceId: viewAgentForm.instance_id,
        userId: agent.id,
        routingProfileId: newRoutingProfileId,
      });
      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to update routing profile');
      }
    } catch (err) {
      // Revert on failure
      setAgents((prev) => prev.map((a) => (
        a.id === agent.id ? { ...a, routing_profile_id: previousProfileId } : a
      )));
      setRowError(err.message || 'Error updating agent routing profile');
    } finally {
      setUpdatingAgentId(null);
    }
  };


  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

    return (
      <div className="setup-page">
       <div className="page-header">
        <div>
            <h1>Reroute Agents</h1>
              <p className="page-subtitle">Reroute Amazon Connect agents</p>
        </div>
        <Link to="/business-admin" className="btn btn-secondary">
          ← Back to Business Administration 
        </Link>
       </div>

      {/* Environment Selector */}
      <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
        >
          {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
        </select>
        <span style={{
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
          {selectedEnvironment}
        </span>
      </div>
        <div className="form-container">
            <form onSubmit={handleViewAgentSubmit} className="setup-form">
                <div className="form-section">
                <h3>Instance Information</h3>
                    <div className="form-group">
                        <label htmlFor="instance_id">Instance *</label>
                        <select
                        id="instance_id"
                        name="instance_id"
                        value={viewAgentForm.instance_id}
                        onChange={handleViewAgentFormChange}
                        required
                        disabled={loadingInstances}
                        >
                        <option value="">
                            {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                        </option>
                        {instances.map((instance) => (
                            <option key={instance.id} value={instance.id}>
                            {instance.name} ({instance.status})
                            </option>
                        ))}
                        </select>
                        <small>Select an Amazon Connect instance</small>
                    </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading || loadingAgents || !viewAgentForm.instance_id}>
                    {loading || loadingAgents ? 'Viewing...' : 'View Agents'}
                  </button>
                  <button type="button" className="btn btn-primary" disabled={loadingProfileQueues || !viewAgentForm.instance_id} onClick={handleViewProfilesAndQueues}>
                    {loadingProfileQueues ? 'Viewing...' : 'View Profiles and Queues'}
                  </button>
              </div>
            </form>
        </div>                

        {profileQueues && showPreview && (
        <div
          className="json-preview-overlay"
          role="presentation"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="json-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="policy-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="json-preview-header">
              <h3 id="policy-preview-title">{previewTitle}</h3>
              <button type="button" className="btn btn-primary" onClick={downloadArchiveDocument}>
                Download (JSON)
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
            <pre className="json-preview-content">
              {JSON.stringify(profileQueues, null, 2)}
            </pre>
          </div>
        </div>
      )}
                
      {(loading || loadingAgents) && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your request...</p>
          </div>
        )}

        {agents.length > 0 && (
          <div className="table-format-list">
            <div className="table-format-list-header">
              <h3>Agents ({sortedAgents.length}{sortedAgents.length !== agents.length ? ` of ${agents.length}` : ''})</h3>
              <div className="table-format-list-controls">
                <input
                  type="text"
                  className="table-format-search-input"
                  placeholder="Search name, username, or email..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
                <div className="page-size-selector">
                  <label htmlFor="page_size">Rows per page:</label>
                  <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                  <th className="sortable" onClick={() => handleSort('username')}>Username{sortIndicator('username')}</th>
                  <th className="sortable" onClick={() => handleSort('email')}>Email{sortIndicator('email')}</th>
                  <th className="sortable" onClick={() => handleSort('last_modified')}>Last Modified{sortIndicator('last_modified')}</th>
                  <th className="sortable" onClick={() => handleSort('routing_profile')}>Routing Profile{sortIndicator('routing_profile')}</th>
                  <th className="sortable" onClick={() => handleSort('security_profile')}>Security Profile{sortIndicator('security_profile')}</th>
                  <th className="sortable" onClick={() => handleSort('phone_type')}>Phone Type{sortIndicator('phone_type')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedAgents.map((agent) => (
                  <tr key={agent.id || agent.username}>
                    <td>{[agent.identity?.first_name, agent.identity?.last_name].filter(Boolean).join(' ') || '-'}</td>
                    <td>{agent.username || '-'}</td>
                    <td>{agent.identity?.email || '-'}</td>
                    <td>{formatTimestamp(agent.last_modified_time) || '-'}</td>
                     <td>
                         <select
                        id={`profile_id-${agent.id}`}
                        name="profile_id"
                        value={agent.routing_profile_id || ''}
                        onChange={(e) => handleAgentRoutingProfileChange(agent, e.target.value)}
                        required
                        disabled={loadingRoutingProfiles || updatingAgentId === agent.id}
                        >
                        <option value="">
                            {loadingRoutingProfiles ? 'Loading profiles...' : 'Select a profile...'}
                        </option>
                        {routingProfiles.map((profile) => (
                            <option key={profile.id} value={profile.id}>
                            {profile.name}
                            </option>
                        ))}
                        </select>
                        {updatingAgentId === agent.id && <small> Saving...</small>}
                    </td>

                    <td>{agent.security_profile_ids?.join(', ') || '-'}</td>
                    <td>{agent.phone_config?.phone_type || '-'}</td>
                   
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
          </div>
        )}
        {!loading && agents.length === 0 && (
          <div className="agent-list">
            <h3>Agents ({agents.length})</h3>
            <p>No agents found.</p>
          </div>
        )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {rowError && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{rowError}</p>
        </div>
      )}

      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          <p>Agent results loaded successfully.</p>
        </div>
      )}

    </div>
    );
};

export default PrototypeAgentReRoutePage;